import React, {useEffect, useState} from "react";
import {IconButton, Menu, MenuItem} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MDBox from "../../../../../../components/MDBox";
import TabPageHeader from "../../header/tabpageheader";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GenericForm from "../../../../../dialog/genericform";
import {getFields} from "../../../../../dialog/formfields";
import DataTableConfig from "../../../../../Tables/DataTableConfig";
import PropTypes from "prop-types";
import useConfigApi from "../../../../../utils/configapi";
import tableData from "../../../../../tabledata";
import Endpoints from "../../../../../utils/endpoints";
import toast from "react-hot-toast";

const HeaderConfig = ({ selectedRowData }) => {
    const { headerConfigColumns } = tableData();
    const { headerConfigFields } = getFields();
    const [headerConfig, setHeaderConfig] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeRowIndex, setActiveRowIndex] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [dialogData, setDialogData] = React.useState(null);
    const [dialogMode, setDialogMode] = React.useState("edit");
    const [dialogTitle, setDialogTitle] = React.useState("Create Header Config");
    const api = useConfigApi();
    const [headerConfigFieldsModified, setHeaderConfigFieldsModified] = useState([]);

    // --- Logo helpers ---
    const sanitizeLogo = (s) =>
      typeof s === "string" ? s.trim() : s ?? "";

    const handleClick = () => {
        setDialogMode("create");
        setDialogData({
          reciptLogo: "",
        });
        setModalOpen(true);
        setDialogTitle("Create Header Config");

    };

    const closeModal = () => {
        setModalOpen(false);
        setDialogData(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setActiveRowIndex(null);
    };

    const handleMenuOpen = (event, index) => {
        setAnchorEl(event.currentTarget);
        setActiveRowIndex(index);
    };

    const handleActionClick = (action: string, item) => {
        const row = headerConfig[activeRowIndex];
        if(action === 'View'){
            setModalOpen(true);
            setDialogMode("view");
            setDialogData({
                    ...row,
                hostReference: row.hostReference.map(s => s.slNo).join(", "),
                reciptLogo: row.reciptLogo || ""
                });
            setDialogTitle("View Header Config");
        } else if (action === 'Edit') {
            setModalOpen(true);
            setDialogMode("edit");
            setDialogTitle("Edit Header Config");
            setDialogData({
              ...row,
              hostReference: row.hostReference.map((s) => s.slNo).join(", "),
              reciptLogo: row.reciptLogo || "",
            });
        } else if (action === 'Delete') {
            setModalOpen(true);
            setDialogMode("delete");
            setDialogTitle("Delete Header Config");
            setDialogData(row);
        }
        handleMenuClose();
    };

    const handleDialogSubmit = (formData) => {
        // console.log("Submitted data:", formData, dialogMode);
        formData.controlConfigSerialNumber  = selectedRowData.slNo;
        formData.hostConfigSerialNumber = formData.hostReference;
        formData.hostConfigSerialNumber = formData.hostReference.split(",")
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .map(Number);
         // Coerce reciptLogo to a plain string for backend (it expects String, not an object)
         if (formData.reciptLogo && typeof formData.reciptLogo === "object") {
           formData.reciptLogo =
           formData.reciptLogo.base64 ||
           formData.reciptLogo.dataURL ||
           formData.reciptLogo.value ||
           "";
        }
        if (typeof formData.reciptLogo === "string") {
            formData.reciptLogo = formData.reciptLogo.trim();
        }
        // Call your API here
        if (dialogMode === "create") {
            // Handle create logic here
            api.post(Endpoints.headerConfig.createOrUpdate, formData)
                .then((response) => {
                    toast.success("Header config successfully created.");
                    fetchHeaderConfigs().catch(console.error);
                })
                .catch((error) => {
                    toast.error("Internal error occurred while creating.");
                });
        } else if (dialogMode === "edit") {
            api
              .put(Endpoints.headerConfig.createOrUpdate, formData)
              .then(({ data }) => {
                toast.success("Header config successfully updated.");
                setModalOpen(false);

                const updated = data?.data ?? formData; // prefer backend echo
                const key = updated?.slNo ?? dialogData?.slNo; // stable id

                setHeaderConfig((prevRows) =>
                  prevRows.map((row) => {
                    if (row.slNo !== key) return row;

                    // host labels: from API (objects) if present, else keep existing
                    const hostReferenceLabels = Array.isArray(updated?.hostReference)
                      ? updated.hostReference.map((s) => s.label)
                      : row.hostReferenceLabels;
                    
                    // normalize logo to data URL for <img src=...>
                    const rawLogo = (updated?.reciptLogo ?? "").toString().trim();
                    const reciptLogo = rawLogo
                      ? rawLogo.startsWith("data:image/")
                        ? rawLogo
                        : `data:image/png;base64,${rawLogo}`
                      : "";
                    const hasLogo = !!reciptLogo;

                    // spread existing first to preserve computed cells like `action`
                    return {
                      ...row,
                      ...updated,
                      hostReferenceLabels,
                      reciptLogo,
                      hasLogo,
                      action: row.action, // Explicitly preserve action column
                    };
                  })
                );

                // do NOT call fetchHeaderConfigs(); keeps order unchanged
              })
              .catch(() => toast.error("Internal error occurred while updating."));
            }
        };

    const handleDialogDelete = (formData) => {
        if (!formData.slNo) {
            toast.error("Missing slNo for deletion.");
            return;
        }
        api.delete(Endpoints.headerConfig.deleteBySlNo(formData.slNo))
            .then(() => {
                toast.success(`Header config ${formData.slNo} successfully deleted.`);
                fetchHeaderConfigs().catch(console.error);
            })
            .catch(() => {
                toast.error("Error deleting header config.");
            });
    };

    useEffect(() => {
        fetchHeaderConfigs().catch(console.error);
    },[]);

    const fetchHeaderConfigs = async () => {
      try {
        // 1. Fetch Control Config
        const headerConfigResponse = await api.get(
          Endpoints.headerConfig.getByControlSlNo(selectedRowData.slNo)
        );
        const headerConfig = headerConfigResponse?.data?.data || [];

        // 2. Fetch Hosts
        const hostResponse = await api.get(
          Endpoints.hostConfig.getByControlSlNo(selectedRowData.slNo)
        );
        const hosts = hostResponse?.data?.data || [];

        // helper: normalize base64 → data URL (if needed)
        const toDataUrl = (b64OrDataUrl) => {
          const raw = (b64OrDataUrl ?? "").toString().trim();
          if (!raw) return "";
          if (raw.startsWith("data:image/")) return raw; // already a data URL
          // assume PNG when backend returns plain base64 string
          return `data:image/png;base64,${raw}`;
        };

        // 3. Format Table Data
        const modifiedData = headerConfig.map((item, index) => {
          const action = (
            <>
              <IconButton onClick={(e) => handleMenuOpen(e, index)}>
                <MoreHorizIcon />
              </IconButton>
            </>
          );

          const hostReferenceLabels = (item.hostReference || []).map((s) => s.label);

          // normalize logo for <img src=...>
          const reciptLogo = toDataUrl(item.reciptLogo);
          const hasLogo = !!reciptLogo;

          return { ...item, action, hostReferenceLabels, reciptLogo, hasLogo };
        });

        setHeaderConfig(modifiedData);

        // 4. Prepare Options for Select Field
        const hostOptions = hosts.map((host) => ({
          label: host.label,
          value: host.slNo,
        }));

        setHeaderConfigFieldsModified(
          headerConfigFields.map((field) =>
            field.name === "hostReference" ? { ...field, options: hostOptions } : field
          )
        );
      } catch (error) {
        toast.error("Error while fetching header config or host data.");
        console.error(error);
      }
    };

    return <>
        <MDBox
            sx={{
                px: 3,
                py: 2,
                bgcolor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            }}
        >
            <TabPageHeader
                title="Header Config"
                showButton={true}
                buttonLabel = "Add"
                onButtonClick={handleClick}

            />
        </MDBox>
        <MDBox sx={{
            px: 3,
            py: 2,
            bgcolor: "#fff",
            borderBottom: "1px solid #d3e3fd",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            overflowX: "auto",
            width: "100%",
        }}>


            <DataTableConfig
                table={{ columns : headerConfigColumns, rows : headerConfig }}
                showTotalEntries={true}
                isSorted={true}
                noEndBorder
                entriesPerPage={true}
                canSearch={true}
            />
        </MDBox>
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
        >
            <MenuItem onClick={() => handleActionClick("View")}>
                <IconButton size="small">
                    <i className="material-icons">visibility</i>
                </IconButton>
                View
            </MenuItem>
            <MenuItem onClick={() => handleActionClick("Edit")}>
                <IconButton size="small">
                    <EditIcon fontSize="small" />
                </IconButton>
                Edit
            </MenuItem>
            <MenuItem onClick={() => handleActionClick("Delete")}>
                <IconButton size="small" color="error">
                    <DeleteIcon fontSize="small" />
                </IconButton>
                Delete
            </MenuItem>
            {/*<MenuItem onClick={() => handleActionClick("Sync")}>*/}
            {/*    <IconButton size="small" color="error">*/}
            {/*        <RefreshIcon fontSize="small" />*/}
            {/*    </IconButton>*/}
            {/*    Sync*/}
            {/*</MenuItem>*/}
        </Menu>
        <GenericForm
            open={isModalOpen}
            handleClose={closeModal}
            title={dialogTitle}
            fields={headerConfigFieldsModified}
            onSubmit={handleDialogSubmit}
            onDelete={handleDialogDelete}
            initialData={dialogData}
            mode={dialogMode}
            useDialog={true}
        />
    </>
};
HeaderConfig.propTypes = {
    selectedRowData: PropTypes.shape({
        controlName: PropTypes.string,
        controlType: PropTypes.string,
        slNo: PropTypes.number,
    }),
};
export default HeaderConfig;
